import { useEffect, useMemo, useState } from 'react';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import {
  TGetOffersResponse,
  TOfferDataRequirement,
} from 'lendingApi/lapiOverrides.types';
import { useNavigate } from 'react-router-dom';
import { TSignupErrorType } from 'types';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useGetOffers } from '../../../lendingApi/lendingApiHooks';
import { useRouterApplicationId } from './useRouterApplicationId';
import { useSignupStep } from './useSignupStep';

type TProps = {
  getOffersEnabled: boolean;
};

export const SIGNUP_ERROR_CODES = {
  CREDIT_CHECK: ['134', '183', '187'],
  CONFIRM_APPLICANT_DETAILS: ['186', '138'],
  DUPLICANT_GUARANTOR: ['136'],
};

const SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES = [
  ...SIGNUP_ERROR_CODES.CREDIT_CHECK,
  ...SIGNUP_ERROR_CODES.CONFIRM_APPLICANT_DETAILS,
];

export const useOffers = ({ getOffersEnabled }: TProps) => {
  const { isUK } = useLocale();
  const { updateLatestAllowedStep, state, stateFetching } = useSignupStep();
  const [isDeciding, setIsDeciding] = useState(false);
  const [signupError, setSignupError] = useState<TSignupErrorType>();
  const [isErrorChecked, setIsErrorChecked] = useState(false);
  const [selectOfferError, setSelectOfferError] = useState(false);
  const [hasSignupDecision, setHasSignupDecision] = useState(false);
  const [isSecurityCheckRequired, setIsSecurityCheckRequired] = useState(false);

  const [documentRequirements, setDocumentRequirements] =
    useState<TOfferDataRequirement['actions']>();

  const navigate = useNavigate();
  const applicationId = useRouterApplicationId();
  const { trackEvent } = useAmplitude();

  const {
    data: offersData,
    refetch: refetchOffersData,
    isRefetching: isRefetchingOffers,
    isLoading: isLoadingOffers,
  } = useGetOffers(getOffersEnabled, applicationId as string);

  useEffect(() => {
    if (
      !offersData ||
      signupError ||
      !applicationId ||
      isLoadingOffers ||
      isRefetchingOffers
    ) {
      return;
    }

    const suggestedOffer = getSuggestedOffer(offersData.data.offers);

    const documentRequirementCategory = suggestedOffer?.data_requirements?.find(
      (requirement) => requirement.category === 'documents',
    );

    setDocumentRequirements(documentRequirementCategory?.actions);
  }, [
    applicationId,
    isLoadingOffers,
    isRefetchingOffers,
    offersData,
    signupError,
  ]);

  useEffect(() => {
    if (
      !isLoadingOffers &&
      !isRefetchingOffers &&
      offersData?.data.offers &&
      !stateFetching
    ) {
      handleSetSignupErrors(offersData.data.offers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offersData, isLoadingOffers, isRefetchingOffers, stateFetching]);

  const getDataRequirementErrors = (
    offers?: TGetOffersResponse['data']['offers'],
  ) => {
    if (!offers) {
      return;
    }

    const errors: { code: string; type: string; detail: string }[] = [];

    offers.forEach((offer) => {
      offer.data_requirements?.forEach((requirement) => {
        requirement.actions.forEach((action) => {
          if (action.status === 'error' && !action.is_optional) {
            action.resolution_methods.forEach((method) => {
              if (method.status === 'error') {
                method.errors.forEach((error) => {
                  errors.push({
                    code: error.code,
                    type: method.type || '',
                    detail: error.detail,
                  });
                });
              }
            });
          }
        });
      });
    });

    return errors;
  };

  const handleRedirect = async (
    offers: TGetOffersResponse['data']['offers'],
  ) => {
    const dataRequirementErrors = getDataRequirementErrors(offers);

    const hasResolveableError = dataRequirementErrors?.some((error) =>
      SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES.includes(error.code),
    );

    if (hasResolveableError && isUK) {
      return navigate(PATH_NAMES.newApplicationCheckDetails({ applicationId }));
    }

    if (hasIndicativeOffers) {
      return;
    }

    const suggestedOffer = getSuggestedOffer(offers);

    const documentsCategory = suggestedOffer?.data_requirements?.find(
      (requirement) => {
        return requirement.category === 'documents';
      },
    );

    const hasDocumentsRequired = documentsCategory?.actions.find((action) => {
      return action.status === 'required' && action.is_optional === false;
    });

    if (!hasDocumentsRequired) {
      navigate(
        PATH_NAMES.application({ applicationId: applicationId as string }),
      );
      return;
    }

    if (hasDocumentsRequired) {
      await updateLatestAllowedStep('new/documents');
      navigate(PATH_NAMES.newApplicationDocuments({ applicationId }), {
        replace: true,
      });
      return;
    }

    setHasSignupDecision(true);
  };

  const handleSignupError = (namedError?: TSignupErrorType) => {
    if (!namedError) {
      setSignupError('technical');
    } else {
      setSignupError(namedError);
    }
    setIsErrorChecked(true);
  };

  const handleSetSignupErrors = (
    offers: TGetOffersResponse['data']['offers'],
  ) => {
    setSignupError(undefined);
    setIsSecurityCheckRequired(false);

    if (!applicationId) {
      return handleSignupError();
    }

    const errors = getDataRequirementErrors(offers);

    if (!errors) {
      return setIsErrorChecked(true);
    }

    const hasAdditionalContacts =
      state?.application?.people && state.application.people.length > 1;

    // handle tracking separately so all errors get logged before returns
    errors.forEach((error) => {
      trackEvent(AMPLITUDE_EVENTS.offersErrored, {
        'Error code': error.code,
        'Method type': error.type,
        'Error detail': error.detail,
      });
    });

    for (const { code } of errors) {
      if (
        // if the error is resolveable, dont set a signup error
        SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES.includes(code) &&
        !hasAdditionalContacts
      ) {
        setIsErrorChecked(true);
        return;
      }

      //  credit_check and confirm_details errors will only be set if the application does not fall into the above block (if it has multiple contacts)
      if (SIGNUP_ERROR_CODES.CREDIT_CHECK.includes(code)) {
        return handleSignupError('credit_check');
      }

      if (SIGNUP_ERROR_CODES.CONFIRM_APPLICANT_DETAILS.includes(code)) {
        return handleSignupError('confirm_details');
      }

      if (SIGNUP_ERROR_CODES.DUPLICANT_GUARANTOR.includes(code)) {
        return handleSignupError('duplicate_guarantor');
      }

      // Flagging enhanced security check for the resolving linked accounts error
      if (code === '150') {
        return setIsSecurityCheckRequired(true);
      }

      return handleSignupError();
    }

    setIsErrorChecked(true);
  };

  const handleDecision = async () => {
    setIsDeciding(true);

    let response;

    try {
      response = await refetchOffersData();
    } catch {
      return handleSignupError();
    }

    const refetchedOffersData = response?.data?.data;

    if (!refetchedOffersData?.offers) {
      return null;
    }

    const suggestedProduct = getSuggestedOffer(refetchedOffersData?.offers);

    const documentsCategory = suggestedProduct?.data_requirements?.find(
      (requirement) => {
        return requirement.category === 'documents';
      },
    );

    const hasDocumentsRequired = documentsCategory?.actions.find((action) => {
      return action.status === 'required' && action.is_optional === false;
    });

    if (!hasDocumentsRequired) {
      navigate(
        PATH_NAMES.application({ applicationId: applicationId as string }),
      );
    }

    setIsDeciding(false);
  };

  const getSuggestedOffer = (offers: TGetOffersResponse['data']['offers']) => {
    return offers?.find((offer) => offer.is_suggested);
  };

  const hasIndicativeOffers =
    offersData?.data.offers &&
    offersData.data.offers.filter((offer) => offer.offer_type === 'indicative')
      .length > 0;

  const hasSampleOffers =
    offersData?.data.offers &&
    offersData.data.offers.filter((offer) => offer.offer_type === 'sample')
      .length > 0;

  const shouldRedirect = () => {
    if (!offersData?.data?.offers || signupError || isSecurityCheckRequired) {
      return false;
    }

    const suggestedOffer = getSuggestedOffer(offersData?.data?.offers);
    const isSampleOffer = suggestedOffer?.offer_type === 'sample';

    const dataRequirementErrors = getDataRequirementErrors(
      offersData.data.offers,
    );

    const hasSelfServeErrorCode = dataRequirementErrors?.some((error) =>
      SELF_SERVE_DATA_REQUIREMENT_ERROR_CODES.includes(error.code),
    );

    return isSampleOffer || (hasIndicativeOffers && hasSelfServeErrorCode);
  };

  const autoBankStatements = useMemo(() => {
    const autoBankStatementFlags = [
      'auto_bs_in_decline',
      'has_invalid_cato_data',
      'lower_score_eligible',
    ];

    return Boolean(
      offersData?.data.offers.find((offer) => {
        return offer.data_requirements?.find((requirement) => {
          return requirement.actions.find((action) => {
            return autoBankStatementFlags.includes(action.reason!);
          });
        });
      }),
    );
  }, [offersData]);

  return {
    applicationId,
    documentRequirements,
    refetchOffersData,
    isRefetchingOffers,
    isLoadingOffers,
    isLoadingState: !state,
    dataRequirementErrors: getDataRequirementErrors(offersData?.data.offers),
    offers: offersData?.data.offers,
    hasIndicativeOffers,
    handleDecision,
    isErrorChecked,
    setIsErrorChecked,
    isSecurityCheckRequired,
    shouldRedirect: shouldRedirect(),
    handleRedirect,
    hasSignupDecision:
      hasSignupDecision || hasIndicativeOffers || hasSampleOffers,
    signupError,
    selectOfferError,
    setSelectOfferError,
    isDeciding,
    autoBankStatements,
  };
};
